/**
 * @file payments.ts
 *
 * The Payments & VAT admin surface. The gateway read/update calls ride the real
 * `/v1/payment-methods` engine passthrough to the payment service: the list is
 * mapped onto the admin {@link PaymentGateway} shape (semantic `cod`/`shore` ids the
 * screens key on), and an update resolves the REAL method id from the list first,
 * then PUTs a read-modify-write merge (the wire bodies require full shapes). The
 * Shore Pay connect rides the real engine route (`POST
 * /v1/payment-methods/shore/connect`), which returns the hosted KYC handoff URL;
 * the old mock `/shop` seam ({@link createShorePayAccount}) is kept only as a
 * reference for the retired details form. VAT is served by the real versioned
 * settings API: it reads
 * `GET /v1/shops/settings` and persists a partial patch to
 * `PATCH /v1/shops/settings/tax`, mapping between the admin {@link VatSettings}
 * shape and the `/v1` `tax` block ({@link toVatSettings} / {@link toTaxUpdate}).
 */

import { shopMutate } from './request';
import { v1Get, v1Mutate } from './v1-request';
import { WcApiError } from './types';
import { MANUAL_GATEWAY_ID, SHORE_GATEWAY_ID } from '../payments/payments-constants';
import type {
  PaymentGateway,
  ShorePayAccountPayload,
  VatSettings,
} from './payments-types';
import type {
  ConnectShorePayRequestV1,
  ConnectShorePayResponseV1,
  ManualPaymentMethodV1,
  PaymentMethodsResponseV1,
  ShopSettingsResponseV1,
  ShorePaymentMethodV1,
  UpdateManualPaymentMethodRequestV1,
  UpdateShorePaymentMethodRequestV1,
  UpdateTaxSettingsRequestV1,
} from './v1-types';

/**
 * Maps a MANUAL method (payment-service shape) to the admin gateway shape. The UI
 * keys on the semantic {@link MANUAL_GATEWAY_ID}; the real method id stays internal
 * to this module (updates re-resolve it from the list).
 *
 * `enableForLocalPickupOnly` has no backend field — the flag is not persisted and is
 * intentionally absent here (it reads as `false` in the form).
 */
function toManualGateway(method: ManualPaymentMethodV1): PaymentGateway {
  return {
    id: MANUAL_GATEWAY_ID,
    methodId: method.id,
    enabled: method.enabled,
    title: method.paymentDetails.name,
    description: method.paymentDetails.info,
    instructions: method.paymentDetails.message ?? '',
    ...(method.paymentDetails.additional !== undefined
      ? { additional: method.paymentDetails.additional }
      : {}),
  };
}

/**
 * Maps a SHORE method to the admin gateway shape. Carries the real method id + `accountStatus`
 * (its presence = an account exists) + `manualCapture` so the overview can branch on
 * connected/pending/active without re-reading, and an update can PUT directly.
 */
function toShoreGateway(method: ShorePaymentMethodV1): PaymentGateway {
  return {
    id: SHORE_GATEWAY_ID,
    methodId: method.id,
    enabled: method.enabled,
    manualCapture: method.manualCapture,
    ...(method.accountStatus !== undefined ? { accountStatus: method.accountStatus } : {}),
  };
}

/** Builds the 404-flavoured error for a gateway that is not connected for the tenant. */
function gatewayNotConnected(id: string): WcApiError {
  return new WcApiError(`Payment method "${id}" is not connected`, 404, 'gateway_not_found');
}

/**
 * Fetches the configured payment gateways (`GET /v1/payment-methods`, mapped).
 * Unconnected methods (null slots) are omitted; the overview treats a missing
 * gateway as inactive.
 *
 * @returns The payment gateways.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchPaymentGateways(): Promise<PaymentGateway[]> {
  const res = await v1Get<PaymentMethodsResponseV1>('/payment-methods');
  const gateways: PaymentGateway[] = [];
  if (res.data.manual) gateways.push(toManualGateway(res.data.manual));
  if (res.data.shore) gateways.push(toShoreGateway(res.data.shore));
  return gateways;
}

/**
 * Updates a payment gateway with a partial patch. The gateway is passed in with its REAL
 * `methodId` (from the initial list read), so this PUTs a read-modify-write merge DIRECTLY —
 * no second GET to re-resolve the id (the caller already loaded it). The `/v1` update bodies
 * are full shapes (`paymentDetails` / `manualCapture` are required), so fields not in the patch
 * are carried over from `current`.
 *
 * @param current - The gateway being edited, carrying `methodId` + its current field values.
 * @param patch - The fields to change (enabled, title, description, …).
 *
 * @returns The full saved gateway (mapped from the PUT response).
 *
 * @throws {WcApiError} When the gateway has no backend method id (not connected) or the request fails.
 */
export async function updatePaymentGateway(
  current: PaymentGateway,
  patch: Partial<PaymentGateway>,
): Promise<PaymentGateway> {
  if (!current.methodId) throw gatewayNotConnected(current.id);

  if (current.id === MANUAL_GATEWAY_ID) {
    const message = patch.instructions ?? current.instructions;
    const body: UpdateManualPaymentMethodRequestV1 = {
      enabled: patch.enabled ?? current.enabled,
      paymentDetails: {
        name: patch.title ?? current.title ?? '',
        info: patch.description ?? current.description ?? '',
        ...(message !== undefined ? { message } : {}),
        ...(current.additional !== undefined ? { additional: current.additional } : {}),
      },
    };
    const updated = await v1Mutate<ManualPaymentMethodV1>(
      `/payment-methods/manual/${current.methodId}`,
      'PUT',
      body,
    );
    return toManualGateway(updated);
  }

  if (current.id === SHORE_GATEWAY_ID) {
    // The UI edits `enabled` only; `manualCapture` is required on the wire, so the
    // current value is carried over.
    const body: UpdateShorePaymentMethodRequestV1 = {
      enabled: patch.enabled ?? current.enabled,
      manualCapture: current.manualCapture ?? false,
    };
    const updated = await v1Mutate<ShorePaymentMethodV1>(
      `/payment-methods/shore/${current.methodId}`,
      'PUT',
      body,
    );
    return toShoreGateway(updated);
  }

  throw gatewayNotConnected(current.id);
}

/**
 * Maps the `/v1` settings `tax` block to the admin {@link VatSettings} shape: a
 * `null` rate becomes an empty string and the numeric rate is stringified for the
 * form's text input. `pricesIncludeTax` is not surfaced in this UI, so it is dropped.
 *
 * @param tax - The `tax` block from a `/v1` shop-settings response.
 *
 * @returns The VAT settings the screen consumes.
 */
export function toVatSettings(tax: ShopSettingsResponseV1['tax']): VatSettings {
  return {
    enabled: tax.vatEnabled,
    standardRate: tax.standardRate === null ? '' : String(tax.standardRate),
    applyToShipping: tax.applyToShipping,
  };
}

/**
 * Maps a partial {@link VatSettings} patch to the `/v1` tax update body: `enabled`
 * is renamed to `vatEnabled`, and the rate string is parsed to a number (a blank
 * rate clears it to `null`). Only provided fields are sent (the contract is a strict
 * partial). The backend rejects enabling VAT without a positive rate in the same patch.
 *
 * @param patch - The partial VAT settings from the form.
 *
 * @returns The `PATCH /v1/shops/settings/tax` request body.
 */
export function toTaxUpdate(patch: Partial<VatSettings>): UpdateTaxSettingsRequestV1 {
  const body: UpdateTaxSettingsRequestV1 = {};
  if ('enabled' in patch) body.vatEnabled = patch.enabled;
  if ('applyToShipping' in patch) body.applyToShipping = patch.applyToShipping;
  if ('standardRate' in patch) {
    const trimmed = (patch.standardRate ?? '').trim();
    // Guard against a non-numeric entry: `Number('12,5')` is `NaN`, which `JSON.stringify`
    // serializes to `null`. Map it to `null` explicitly so the intent (no rate) is clear
    // rather than smuggling a `NaN` into the typed body. The form validates the rate first;
    // this is defence in depth for any other caller.
    const parsed = Number(trimmed);
    body.standardRate = trimmed === '' || !Number.isFinite(parsed) ? null : parsed;
  }
  return body;
}

/**
 * Fetches the VAT settings from the shop's `/v1` settings (the `tax` block).
 *
 * @returns The VAT settings.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchVatSettings(): Promise<VatSettings> {
  const res = await v1Get<ShopSettingsResponseV1>('/shops/settings');
  return toVatSettings(res.data.tax);
}

/**
 * Updates the VAT settings with a partial patch (`PATCH /v1/shops/settings/tax`).
 *
 * @param patch - The fields to change (enabled, standardRate, applyToShipping).
 *
 * @returns The full saved VAT settings.
 *
 * @throws {WcApiError} When the request fails (including the backend's "enabling VAT
 *   requires a rate greater than 0" rule).
 */
export async function updateVatSettings(
  patch: Partial<VatSettings>,
): Promise<VatSettings> {
  const settings = await v1Mutate<ShopSettingsResponseV1>(
    '/shops/settings/tax',
    'PATCH',
    toTaxUpdate(patch),
  );
  return toVatSettings(settings.tax);
}

/**
 * Connects a Shore Pay account from the supplied business details.
 *
 * @param payload - The business details for the account.
 *
 * @returns The submitted details (echoed by the mock seam).
 *
 * @throws {WcApiError} When the request fails.
 */
export async function createShorePayAccount(
  payload: ShorePayAccountPayload,
): Promise<ShorePayAccountPayload> {
  return shopMutate<ShorePayAccountPayload>(
    '/payments/shore-pay/account',
    'POST',
    payload,
  );
}

/**
 * Starts (or resumes) the interactive Shore Pay onboarding
 * (`POST /v1/payment-methods/shore/connect`). On a FIRST connect, pass the merchant
 * `email` + ISO alpha-2 `country` (collected by the connect form) — the engine forwards
 * them to the payment-service, which mints the Shore/Stripe hosted KYC URL. To RESUME an
 * existing account (the connected-state KYC link), call with no argument: the engine
 * regenerates the KYC link on the same account and needs no details. The caller redirects
 * the browser to `actionUrl`; activation arrives asynchronously via webhook and shows up
 * on the overview list.
 *
 * @param payload - Merchant email + country for a first connect; omit to resume.
 *
 * @returns The hosted onboarding URL + Shore method id.
 *
 * @throws {WcApiError} When the request fails (including "already connected").
 */
export async function connectShorePay(
  payload?: ConnectShorePayRequestV1,
): Promise<ConnectShorePayResponseV1> {
  return v1Mutate<ConnectShorePayResponseV1>(
    '/payment-methods/shore/connect',
    'POST',
    payload,
  );
}
